'use client';

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Image } from '@zoomit/primitives';
import { Box, Flex, Skeleton } from '@zoomit/primitives';
import { BaseMediaUrl } from '@zoomit/web-kit';
import { Hr, theme } from '@zoomit/zoomit-ds';

import { getStickerPacks } from '../../../apis/feedbacks';

type Props = {
  select: ({ imageId }: { imageId: string }) => void;
};

export const StickerPicker = (props: Props) => {
  const { select } = props;
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const { data, isLoading, isError, isSuccess } = useQuery({
    queryFn: async () => {
      return getStickerPacks();
    },
    queryKey: ['sticker', 'pack'],
    select: ({ data }) => data,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  if (isError) return null;
  if (isSuccess && data.length === 0) return null;

  return (
    <Root flexDirection={'column'} gap={1.2} spacing={{ p: 'x4' }}>
      <Flex flexDirection={'column'}>
        <Flex gap={1.8}>
          {isLoading &&
            Array.from({ length: 3 }).map((_, idx) => (
              <Category $isActive={false} key={`${idx}-skeleton-category`}>
                <Skeleton width={2.4} height={2.4} />
              </Category>
            ))}
          {isSuccess &&
            data.map(({ id, name, imageIds }, idx) => (
              <Category
                onClick={() => setSelectedCategoryIndex(idx)}
                $isActive={idx === selectedCategoryIndex}
                key={id}
              >
                <Image
                  width={24}
                  height={24}
                  src={`${BaseMediaUrl}/${imageIds[0]}`}
                  alt={`sticker-${id}-${name}`}
                />
              </Category>
            ))}
        </Flex>
        <Hr style={{ marginTop: 8 }} />
      </Flex>

      <Box
        className={'no-scrollbar relative grid h-52 grid-cols-4 place-content-start gap-2 overflow-y-auto'}
      >
        {isLoading &&
          Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton width={6.4} height={6.4} key={`${idx}-skeleton-sticker`} />
          ))}

        {isSuccess &&
          data?.[selectedCategoryIndex]?.imageIds.map((stickerId) => (
            <Image
              style={{ cursor: 'pointer' }}
              onClick={() => {
                select({ imageId: stickerId });
              }}
              alt={`sticker-${stickerId}`}
              src={`${BaseMediaUrl}/${stickerId}`}
              width={64}
              height={64}
            />
          ))}
      </Box>
    </Root>
  );
};

const Root = styled(Flex)`
  background-color: var(${theme.card});
  border-radius: var(${theme.radius.md});
`;

const Category = styled(Flex)<{ $isActive: boolean }>`
  position: relative;
  cursor: pointer;

  ${({ $isActive }) =>
    $isActive &&
    css`
      &::before {
        content: '';
        width: 32px;
        height: 3px;
        position: absolute;
        background: var(${theme.primary1});
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-radius: var(${theme.radius.md});
      }
    `}
`;
